import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import Typography from "@material-ui/core/Typography";
import _ from "lodash";
import PropTypes from "prop-types";
import React, {Fragment, memo, useRef, useState} from "react";
import * as XLSX from "xlsx";
import {EXCEL_EXTENSION, IOS_RESOURCE_FILENAME, KEY, TEMPLATE_FILE_NAME} from "../constant";
import AndroidExcelWriter from "../writers/excel/AndroidExcelWriter";
import FlutterExcelWriter from "../writers/excel/FlutterExcelWriter";
import IOSExcelWriter from "../writers/excel/IOSExcelWriter";
import BootstrapInput from "./common/BootstrapInput";
import PhpLaravelExcelWriter from "../writers/excel/PhpLaravelExcelWriter";
import I18NextExcelWriter from "../writers/excel/I18NextExcelWriter";

const ExcelGenerator = ({languageCodes, onLoad, keyMacro, valueMacro}) => {
    const fileInput = useRef();
    const directoryInput = useRef();
    const [resource, setResource] = useState("");

    const handleFileInputChange = event => {
        directoryInput.current.value = "";
        setResource(_.get(event, "target.files[0].name", ""));
    };

    const handleDirectoryInputChange = event => {
        fileInput.current.value = "";
        setResource(`~/${_.get(event, "target.files.0.webkitRelativePath", "").split("/")[0]}...`);
    };

    const handleConvertToExcel = async () => {
        let keyMacro = getKeyMacro();
        let valueMacro = getValueMacro();
        onLoad(true);
        const file = _.get(fileInput, "current.files[0]");
        if (file) {
            if (file.name.endsWith(".xml")) {
                await new AndroidExcelWriter(languageCodes, keyMacro, valueMacro).generateFromFile(file);
            } else if (file.name.endsWith(".strings")) {
                await new IOSExcelWriter(languageCodes, keyMacro, valueMacro).generateFromFile(file);
            } else if (file.name.endsWith(".arb")) {
                await new FlutterExcelWriter(languageCodes, keyMacro, valueMacro).generateFromFile(file);
            } else if (file.name.endsWith(".json")) {
                await new I18NextExcelWriter(languageCodes, keyMacro, valueMacro).generateFromFile(file);
            } else if (file.name.endsWith(".php")) {
                await new PhpLaravelExcelWriter(languageCodes, keyMacro, valueMacro).generateFromFile(file);
            }
            onLoad(false);
            return;
        }
        const files = _.get(directoryInput, "current.files");
        if (files && files.length > 0) {
            const androidWriter = new AndroidExcelWriter(languageCodes, keyMacro, valueMacro);
            const iosWriter = new IOSExcelWriter(languageCodes, keyMacro, valueMacro);
            const flutterWriter = new FlutterExcelWriter(languageCodes, keyMacro, valueMacro);
            const i18NextWriter = new I18NextExcelWriter(languageCodes, keyMacro, valueMacro);
            const phpLaravelWriter = new PhpLaravelExcelWriter(languageCodes, keyMacro, valueMacro);
            for (const propertyName in files) {
                const file = files[propertyName];
                if (file.webkitRelativePath) {
                    if (file.webkitRelativePath.endsWith(".xml")) {
                        androidWriter.files.push(file);
                    } else if (file.webkitRelativePath.endsWith(".strings")) {
                        iosWriter.files.push(file);
                    } else if (file.webkitRelativePath.endsWith(".arb")) {
                        flutterWriter.files.push(file);
                    } else if (file.webkitRelativePath.endsWith(".json")) {
                        i18NextWriter.files.push(file);
                    } else if (file.webkitRelativePath.endsWith(".php")) {
                        phpLaravelWriter.files.push(file);
                    }
                }
            }
            let data = {};
            if (androidWriter.files.length > 0) {
                data = await androidWriter.mergeWithFiles(data);
            }
            if (iosWriter.files.length > 0) {
                data = await iosWriter.mergeWithFiles(data);
            }
            if (flutterWriter.files.length > 0) {
                data = await flutterWriter.mergeWithFiles(data);
            }
            if (i18NextWriter.files.length > 0) {
                data = await i18NextWriter.mergeWithFiles(data);
            }
            if (phpLaravelWriter.files.length > 0) {
                data = await phpLaravelWriter.mergeWithFiles(data);
            }
            const workbook = XLSX.utils.book_new();
            for (const sheetName in data) {
                const sheet = XLSX.utils.aoa_to_sheet(data[sheetName]);
                XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
            }
            XLSX.writeFile(workbook, `${TEMPLATE_FILE_NAME}.${EXCEL_EXTENSION}`);
            onLoad(false);
            return;
        }
        alert("Please select a directory containing app resources or a resource file.");
        onLoad(false);
    };

    const handleGenerateEmptyExcel = () => {
        const data = [[KEY, ...languageCodes.map(code => code.name)]];
        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.aoa_to_sheet(data);
        XLSX.utils.book_append_sheet(workbook, sheet, IOS_RESOURCE_FILENAME.replace(".strings", ""));
        XLSX.writeFile(workbook, `${TEMPLATE_FILE_NAME}.${EXCEL_EXTENSION}`);
    };

    const getKeyMacro = () => {
        try {
            const macroFunction = eval(keyMacro);
            if (typeof macroFunction === "function") {
                return macroFunction;
            }
        } catch (error) {
            console.log(error.message);
        }
        return null;
    };

    const getValueMacro = () => {
        try {
            const macroFunction = eval(valueMacro);
            if (typeof macroFunction === "function") {
                return macroFunction;
            }
        } catch (error) {
            console.log(error.message);
        }
        return null;
    };

    return (<Fragment>
        <Typography variant="h4" gutterBottom>Convert app resources to excel</Typography>
        <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={12} sm={8} md={8}>
                <BootstrapInput value={resource}
                                placeholder="Select a file or directory"
                                fullWidth
                                readOnly/>
                <Input inputRef={directoryInput}
                       onChange={handleDirectoryInputChange}
                       id="resourceDirectory"
                           type="file"
                           inputProps={{webkitdirectory: "true"}}/>
                    <Input inputRef={fileInput}
                           onChange={handleFileInputChange}
                           accept="text/xml,.strings,.arb"
                           id="resourceFile"
                           type="file"/>
                </Grid>
                <Grid item xs={12} sm={4} md={4}>
                    <InputLabel htmlFor="resourceDirectory">
                        <Button component="span"
                                variant="contained"
                                color="primary"
                                fullWidth>
                            Select directory
                        </Button>
                    </InputLabel>
                </Grid>
            </Grid>
            <Grid container spacing={2} justify="flex-end">
                <Grid item xs={12} sm={4} md={4}>
                    <InputLabel htmlFor="resourceFile">
                        <Button component="span"
                                variant="contained"
                                color="primary"
                                fullWidth>
                            Select file
                        </Button>
                    </InputLabel>
                </Grid>
            </Grid>
            <Grid container spacing={2} justify="flex-end">
                <Grid item xs={12} sm={4} md={4}>
                    <Button onClick={handleConvertToExcel}
                            variant="contained"
                            color="primary"
                            fullWidth>
                        Convert to excel
                    </Button>
                </Grid>
            </Grid>
        <Grid container spacing={2} justify="flex-end">
            <Grid item xs={12} sm={4} md={4}>
                <Button onClick={handleGenerateEmptyExcel}
                        variant="contained"
                        color="primary"
                        fullWidth>
                    Template excel
                </Button>
            </Grid>
        </Grid>
    </Fragment>);
};

ExcelGenerator.propTypes = {
    languageCodes: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string,
        android: PropTypes.string,
        ios: PropTypes.string,
        flutter: PropTypes.string,
        phpLaravel: PropTypes.string
    })), keyMacro: PropTypes.string, valueMacro: PropTypes.string, onLoad: PropTypes.func
};

export default memo(ExcelGenerator);
